/**
 * Phase 32 — storefront context.
 *
 * The customer-facing app is split into two storefronts, "spain" and "camps",
 * mounted at URL prefixes `/es/*` and `/camp/*`. This context centralises the
 * `shop` derivation from the URL so any page/component can:
 *
 *   const {shop} = useShop();
 *   api.get("/products", {params: {shop, ...}});
 *
 * The splash selector (`/`) is intentionally *un-shopped* — no context value
 * is provided there and consumers must handle `shop === null`.
 *
 * A cookie (`mercago_shop`, 30 days) mirrors the last selected shop so:
 *   • legacy URLs (`/producto/<slug>`) can redirect intelligently.
 *   • the splash can badge the "última visita" hint.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { conditionalGet } from "./conditional-fetch";

const ShopContext = createContext(null);

const COOKIE_NAME = "mercago_shop";
const COOKIE_MAX_AGE_DAYS = 30;

export function getShopCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const val = decodeURIComponent(match.split("=")[1] || "");
  return val === "envio" || val === "local" ? val : null;
}

export function setShopCookie(shop) {
  if (typeof document === "undefined") return;
  if (shop !== "envio" && shop !== "local") return;
  const maxAge = 60 * 60 * 24 * COOKIE_MAX_AGE_DAYS;
  const secure =
    typeof window !== "undefined" && window.location?.protocol === "https:";
  document.cookie =
    `${COOKIE_NAME}=${shop}; path=/; max-age=${maxAge}; samesite=lax` +
    (secure ? "; secure" : "");
}

export function shopFromPath(pathname) {
  if (!pathname) return null;
  if (pathname === "/envio" || pathname.startsWith("/envio/")) return "envio";
  if (pathname === "/local" || pathname.startsWith("/local/")) return "local";
  return null;
}

export const SHOP_META = {
  envio: {
    code: "envio",
    name: "Comprar en España y enviar",
    short_name: "Envío",
    icon: "🇪🇸",
    prefix: "/envio",
  },
  local: {
    code: "local",
    name: "Comprar en los campamentos",
    short_name: "Local",
    icon: "🥫",
    prefix: "/local",
  },
};

/** The default shop when we can't infer one from URL or cookie. */
export const DEFAULT_SHOP = "envio";

/**
 * Wraps routes that live under `/es` or `/camp`. Mirrors the URL-derived shop
 * into the cookie so we can navigate back later even from `/`.
 */
export function ShopProvider({ shop, children }) {
  useEffect(() => {
    if (shop) setShopCookie(shop);
  }, [shop]);

  // Fase 3 · Catalog kill-switch — refrescamos el estado al montar y cada
  // 60 s para que un cambio del admin llegue al cliente sin recarga manual.
  const [catalogHidden, setCatalogHidden] = useState(false);
  useEffect(() => {
    if (!shop) return undefined; // fuera de tienda (splash) no hace falta
    let alive = true;
    // Usamos ``conditionalGet`` en vez de ``api.get`` directo — envía
    // ``If-None-Match`` en cada poll y el servidor puede responder 304
    // (sin body) cuando el flag ``catalog_hidden`` no ha cambiado.
    const fetchVisibility = () =>
      conditionalGet("/settings/catalog-visibility")
        .then(({ data }) => alive && setCatalogHidden(!!data?.catalog_hidden))
        .catch(() => alive && setCatalogHidden(false));
    fetchVisibility();
    const iv = setInterval(fetchVisibility, 60000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [shop]);

  const value = useMemo(() => {
    if (!shop) return null;
    return { shop, ...SHOP_META[shop], catalog_hidden: catalogHidden };
  }, [shop, catalogHidden]);

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

/** Returns the current shop context, or `null` when outside a shop route. */
export function useShop() {
  return useContext(ShopContext);
}

/** Returns a builder that prefixes a path with the current shop route.
 *  When there is no shop (e.g. splash), the path is returned unchanged so
 *  shared components (like the search box) still resolve to a sane URL.
 */
export function useShopHref() {
  const ctx = useShop();
  return (path) => {
    if (!ctx) return path;
    if (!path || path === "/") return ctx.prefix + "/";
    return `${ctx.prefix}${path.startsWith("/") ? path : `/${path}`}`;
  };
}

/** Helper used by App.js redirect logic — the current pathname is the source
 *  of truth. Returns `null` when the caller is already inside a shop route.
 */
export function inferShopForRedirect(pathname) {
  if (shopFromPath(pathname)) return null;
  return getShopCookie();
}
